import { Tooltip } from '@highlight/ui'
import { memo } from 'react'
import { Info } from 'lucide-react'

export interface ActionWarningProps {
  warning: string
}

/**
 * Renders a staged action's `view.invokeWarning` — what approving it commits the
 * user to (e.g. "Lets Highlight approve its own Linear actions").
 *
 * Takes the source attribution's slot in the footer while an action is still
 * awaiting approval, so it inherits that slot's sizing: the text truncates
 * rather than wrapping, keeping the invoke button hard right no matter how long
 * the warning or how narrow the panel. The tooltip carries the full text, since
 * a warning the user can't finish reading isn't doing its job.
 *
 * Every ancestor between here and the cell must be `min-w-0` for the truncation
 * to resolve — `truncate` sets `white-space: nowrap`, which makes the full
 * string the min-content width and expands any flex parent still on the default
 * `min-width: auto`.
 */
export const ActionWarning = memo(function ActionWarning({ warning }: ActionWarningProps) {
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={<div className="text-warning-subtle flex min-w-0 flex-1 items-center gap-1 font-medium" />}
      >
        <Info className="size-3.5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{warning}</span>
      </Tooltip.Trigger>
      <Tooltip.Content>{warning}</Tooltip.Content>
    </Tooltip>
  )
})
